import { AuthCredentials, AuthRepository } from '../repositories/AuthRepository';
import { LoginDto, RegisterDto, SignupDto, ResetPasswordDto, ForgotUsernameDto, VerifyCodeDto, UpdatePasswordDto, UpdateUserDto } from '../../infrastructure/dto/AuthDto';

import { AuthDomainService } from '../services/AuthDomainService';

/**
 * Use case for authentication operations
 * Orchestrates the domain model and interfaces with repositories
 */
export class AuthUseCase {
  constructor(
    private readonly authRepository: AuthRepository,
    private readonly authDomainService: AuthDomainService,
  ) {}

  /**
   * Handles user login
   */
  async login(loginDto: LoginDto): Promise<string> {
    // Optional: Validate credentials before passing to repository
    // const validationResult = this.authDomainService.validateCredentialStrength(loginDto.password);

    const credentials: AuthCredentials = {
      userName: loginDto.userName,
      password: loginDto.password,
    };

    const response = await this.authRepository.login(credentials);
    return response.token;
  }

  /**
   * Logs the user out
   */
  async logout(): Promise<void> {
    await this.authRepository.logout();
  }

  /**
   * Registers a new user
   */
  async register(registerDto: RegisterDto): Promise<void> {
    // Validate password strength
    const validationResult = this.authDomainService.validateCredentialStrength(registerDto.password);
    if (!validationResult.isValid) {
      throw new Error(validationResult.message);
    }

    return this.authRepository.register(
      registerDto.email,
      registerDto.password,
      registerDto.firstName,
      registerDto.lastName,
      registerDto.activationCode,
      registerDto.username,
      registerDto.phone,
    );
  }

  /**
   * Signs up a new user without activation code
   */
  async signup(signupDto: SignupDto): Promise<void> {
    // Validate password strength
    const validationResult = this.authDomainService.validateCredentialStrength(signupDto.password);
    if (!validationResult.isValid) {
      throw new Error(validationResult.message);
    }

    return this.authRepository.signup(
      signupDto.email,
      signupDto.password,
      signupDto.firstName,
      signupDto.lastName,
      signupDto.username,
      signupDto.phone,
    );
  }

  /**
   * Requests a password reset
   */
  async resetPassword(resetPasswordDto: ResetPasswordDto): Promise<void> {
    return this.authRepository.resetPassword(resetPasswordDto.username);
  }

  /**
   * Requests username recovery
   */
  async forgotUsername(forgotUsernameDto: ForgotUsernameDto): Promise<void> {
    return this.authRepository.forgotUsername(forgotUsernameDto.email);
  }

  /**
   * Verifies verification code
   */
  async verifyCode(verifyCodeDto: VerifyCodeDto): Promise<number> {
    return this.authRepository.verifyCode(verifyCodeDto.username, verifyCodeDto.verificationCode);
  }

  /**
   * Updates user password
   */
  async updatePassword(updatePasswordDto: UpdatePasswordDto): Promise<void> {
    return this.authRepository.updatePassword(updatePasswordDto.globalUserId, updatePasswordDto.password, updatePasswordDto.verificationToken);
  }

  /**
   * Updates user profile
   */
  async updateUser(updateUserDto: UpdateUserDto): Promise<void> {
    return this.authRepository.updateUser(
      updateUserDto.globalUserId,
      updateUserDto.firstName,
      updateUserDto.lastName,
      updateUserDto.email,
      updateUserDto.phone,
    );
  }

  /**
   * Verifies if a token is valid
   */
  verifyToken(token: string): boolean {
    return this.authDomainService.verifyToken(token);
  }
}
